import { Component, Host, h, Prop, State, Element, Event, Method, Watch, forceUpdate, Build } from '@stencil/core';
import type { ComponentInterface, EventEmitter } from '@stencil/core';
import type {Attributes, AutocompleteTypes} from "../../common/interface"
import { inheritAriaAttributes, inheritAttributes, debounceEvent } from '@utils/helpers';
import { createSlotMutationController } from '@utils/slot-mutation-controller';
import type { SlotMutationController } from '@utils/slot-mutation-controller';
import type { NotchController } from '@utils/forms/index';
import { createNotchController } from '@utils/forms/index';
import { getCounterText, hostContext, StyleEventDetail } from './input.utils';
import type {TextFieldTypes} from './input.utils';
export interface Rule {
  required?: boolean;
  message?: string; // 错误提示
  validator?: (value: string | number) => boolean; // 校验规则
}

export interface CustomEvent {
  change: (e: { detail: { value: string } }) => void;
  focus?: () => void;
  blur?: () => void;
}

@Component({
  tag: 'jackery-field',
  styleUrl: 'jackery-field.scss',
  scoped: true
})
export class JackeryField implements ComponentInterface {
  private nativeInput?: HTMLInputElement;
  private inputId = `jackery-field-${inputIds++}`;
  private inheritedAttributes: Attributes = {};
  private isComposing = false;
  private slotMutationController?: SlotMutationController;
  private notchController?: NotchController;
  private notchSpacerEl: HTMLElement | undefined;
  private originalJackeryInput?: EventEmitter<any>;

  /**
   * `true` if the input was cleared as a result of the user typing
   * with `clearOnEdit` enabled.
   *
   * Resets when the input loses focus.
   */
  private didInputClearOnEdit = false;

  /**
   * input focused时候的value
   */
  private focusedValue?: string | number | null;

  @State() hasFocus = false;

  @Element() element!: HTMLJackeryFieldElement;

  /**
   * The color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   * For more information on colors, see [theming](/docs/theming/basics).
   */
  @Prop({ reflect: true }) color?: string;

  /**
   * 指示文本值在输入/编辑时是否以及如何自动大写。
   * 可用选项: `"off"`, `"none"`, `"on"`, `"sentences"`, `"words"`, `"characters"`.
   */
  @Prop() autocapitalize = 'off';

  /**
   * 是否启用自动纠错
   */
  @Prop() autocorrect: 'on' | 'off' = 'off';

  /**
   * 控件的值是否可以由浏览器自动补全。
   */
  @Prop() autocomplete: AutocompleteTypes = 'off';

  /**
   * 自动聚焦
   */
  @Prop() autofocus = false;

  /**
   * 设置为true，出现一键清除内容按钮
   */
  @Prop() clearInput = false;

  /**
   * 如果`true`，该值将在编辑后被清除。当`type`为`password`时默认为`true`，其他类型为`false`。
   */
  @Prop() clearOnEdit?: boolean;

  /**
   * 如果为`true`，字符计数器将显示字符数和总字符限制。还必须设置`maxlength`属性，以便正确计算计数器。
   */
  @Prop() counter = false;

  /**
   * 处理计算器文本的回调函数.
   * 默认计数器格式为 `itemLength / maxLength`.
   */
  @Prop() counterFormatter?: (inputLength: number, maxLength: number) => string;

  /**
   * 每次触发' jackeryInput '事件所需的等待时间(以毫秒为单位)。
   */
  @Prop() debounce?: number;
  @Watch('debounce')
  protected debounceChanged() {
    const { jackeryInput, debounce, originalJackeryInput } = this;
    /**
     * If debounce is undefined, we have to manually revert the jackeryInput emitter in case
     * debounce used to be set to a number. Otherwise, the event would stay debounced.
     */
    this.jackeryInput = debounce === undefined ? originalJackeryInput ?? jackeryInput : debounceEvent(jackeryInput, debounce);
  }

  /**
   * 禁用控件
   */
  @Prop() disabled = false;
  @Watch('disabled')
  protected disabledChanged() {
    this.emitStyle();
  }

  /**
   * html属性：可以修改键盘enter键显示.
   * 可选值: `"enter"`, `"done"`, `"go"`, `"next"`,
   * `"previous"`, `"search"`, and `"send"`.
   */
  @Prop() enterkeyhint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';

  /**
   * 错误显示信息
   */
  @Prop() errorText?: string;

  /**
   * 帮助提示，没有检测到错误文本时候显示
   */
  @Prop() helperText?: string;

  /**
   * 填充. 如果为 `"solid"` 设置背景. 如果为
   * `"outline"` 透明并带边框.
   */
  @Prop() fill?: 'outline' | 'solid';

  /**
   * 控件键盘显示类型.
   * 可选值: `"none"`, `"text"`, `"tel"`, `"url"`,
   * `"email"`, `"numeric"`, `"decimal"`, and `"search"`.
   */
  @Prop() inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

  /**
   * 控件标签
   * 如果同时使用`label`属性，`label`属性将优先于`label`插槽。
   */
  @Prop() label?: string;

  /**
   * 标签label相对于控件的位置.
   * `"start"`: label在控件的左边.
   * `"end"`: 位于控件右边
   * `"floating"`: 聚焦时候标签缩小到控件上方
   * `"stacked"`: 一直缩小在控件上方显示
   * `"fixed"`: 与start相同，但有固定宽度，长文本会省略显示
   */
  @Prop() labelPlacement: 'start' | 'end' | 'floating' | 'stacked' | 'fixed' = 'start';

  /**
   * 最大值 - 不能小于min
   */
  @Prop() max?: string | number;

  /**
   *最大字符限制数：当type属性为 `text`, `email`, `search`, `password`, `tel`, 或 `url`, 可以指定用户最大输入字符数.
   */
  @Prop() maxlength?: number;

  /**
   * 最小值 - 不能大于max.
   */
  @Prop() min?: string | number;

  /**
   * 最小字符限制数：当type属性为 `text`, `email`, `search`, `password`, `tel`, 或 `url`, 可以指定用户最小输入字符数.
   */
  @Prop() minlength?: number;

  /**
   * 为true时可以输入多个值。当type属性为`email`时，此属性生效，否则将被忽略。
   */
  @Prop() multiple?: boolean;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name: string = this.inputId;

  @Prop() pattern?: string;

  @Prop() defaultvalue?: string;

  /**
   * 指示性文本-placeholder
   * 此属性只适用于 当属性 `type` 为 `"email"`,
   * `"number"`, `"password"`, `"search"`, `"tel"`, `"text"`, or `"url"`时, 否则会被忽略.
   */
  @Prop() placeholder?: string;

  /**
   * 只读属性
   */
  @Prop() readonly = false;

  /**
   * 是否必填.
   */
  @Prop() required = false;

  /**
   * input的形状，round圆角大
   */
  @Prop() shape?: 'round';

  /**
   * 是否检查拼写语法
   */
  @Prop() spellcheck = false;

  /**
   * 步进增量
   */
  @Prop() step?: string;

  @Prop() size?: number;

  /**
   * 控件显示类型，默认text.
   */
  @Prop() type: TextFieldTypes = 'text';

  @Prop() errormsg?: string;

  /**
   * value
   */
  @Prop({ mutable: true }) value?: string | number | null = '';

  @State() rules: Rule[] = [];
  @State() showError : boolean = false;
  inputRef: any;
  errorRef: any;

  /**
   * 每次修改输入值触发`jackeryInput`事件.
   * 与`jackeryChange`不同, `jackeryInput`在每次更改时候触发
   */
   @Event() jackeryInput!: EventEmitter<any>;

  /**
   * `jackeryChange` event
   */
  @Event() jackeryChange!: EventEmitter<any>;

  /**
   * 失去焦点触发
   */
  @Event() jackeryBlur!: EventEmitter<FocusEvent>;

  /**
   * 聚焦触发
   */
  @Event() jackeryFocus!: EventEmitter<FocusEvent>;

  /**
   * Emitted when the styles change.
   * @internal
   */
  @Event() ionStyle!: EventEmitter<StyleEventDetail>;

  /**
   * Update the item classes when the placeholder changes
   */
  @Watch('placeholder')
  protected placeholderChanged() {
    this.emitStyle();
  }

  /**
   * Emits an `jackeryChange` event.
   *
   * This API should be called for user committed changes.
   * This API should not be used for external value changes.
   */
   private emitValueChange(event?: Event) {
    const { value } = this;
    const newValue = value == null ? value : value.toString();
    this.focusedValue = newValue;
    this.jackeryChange.emit({ value: newValue, event });
  }

  /**
   * Emits an `ionInput` event.
   */
  private emitInputChange(event?: Event) {
      const { value } = this;
  
      // Checks for both null and undefined values
      const newValue = value == null ? value : value.toString();
  
      this.jackeryInput.emit({ value: newValue, event });
  }

  /**
   * 渲染helperText 和 errorText
   */
  private renderHintText() {
    const { helperText, errorText } = this;

    return [<div class="helper-text">{helperText}</div>, <div class="error-text">{errorText}</div>];
  }

  private renderCounter() {
    const { counter, maxlength, counterFormatter, value } = this;
    if (counter !== true || maxlength === undefined) return
    return <div class="counter">{getCounterText(value, maxlength, counterFormatter)}</div>;
  }

  private onInput = (ev: InputEvent | Event) => {
    const input = ev.target as HTMLInputElement | null;
    if (input) {
      this.value = input.value || '';
    }
    this.emitInputChange(ev);
  };

  private onChange = (ev: Event) => {
    this.emitValueChange(ev);
  };

  private onBlur = (ev: FocusEvent) => {
    this.hasFocus = false;
    this.emitStyle();

    if (this.focusedValue !== this.value) {
      this.emitValueChange(ev);
    }

    this.didInputClearOnEdit = false;
    this.jackeryBlur.emit(ev);
  };

  private onFocus = (ev: FocusEvent) => {
    this.hasFocus = true;
    this.focusedValue = this.value;
    this.emitStyle();
    this.jackeryFocus.emit(ev);
  };

  private shouldClearOnEdit() {
    const { type, clearOnEdit } = this;
    return clearOnEdit === undefined ? type === 'password' : clearOnEdit;
  }

  private emitStyle() {
    // if (this.legacyFormController.hasLegacyControl()) {
    //   this.ionStyle.emit({
    //     interactive: true,
    //     input: true,
    //     'has-placeholder': this.placeholder !== undefined,
    //     'has-value': this.hasValue(),
    //     'has-focus': this.hasFocus,
    //     'interactive-disabled': this.disabled,
    //   });
    // }
  }

  private getValue(): string {
    return typeof this.value === 'number' ? this.value.toString() : (this.value || '').toString();
  }

  private onCompositionStart = () => {
    this.isComposing = true;
  };

  private onCompositionEnd = () => {
    this.isComposing = false;
  };

  private clearTextInput = (ev?: Event) => {
    if (this.clearInput && !this.readonly && !this.disabled && ev) {
      ev.preventDefault();
      ev.stopPropagation();
      // Attempt to focus input again after pressing clear button
      // this.setFocus();
    }
    this.value = '';
    this.emitInputChange(ev);
  };

  private onKeydown = (ev: KeyboardEvent) => {
    this.checkClearOnEdit(ev);
  };

  private checkClearOnEdit(ev: KeyboardEvent) {
    if (!this.shouldClearOnEdit()) {
      return;
    }
    /**
     * Clear the input if the control has not been previously cleared during focus.
     * Do not clear if the user hitting enter to submit a form.
     */
    if (!this.didInputClearOnEdit && this.hasValue() && ev.key !== 'Enter' && ev.key !== 'Tab') {
      this.value = '';
      this.emitInputChange(ev);
    }
    this.didInputClearOnEdit = true;
  }

  private hasValue(): boolean {
    return this.getValue().length > 0;
  }

  /**
   * Responsible for rendering helper text,
   * error text, and counter. This element should only
   * be rendered if hint text is set or counter is enabled.
   */
   private renderBottomContent() {
    const { counter, helperText, errorText, maxlength } = this;

    /**
     * undefined and empty string values should
     * be treated as not having helper/error text.
     */
    const hasHintText = !!helperText || !!errorText;
    const hasCounter = counter === true && maxlength !== undefined;
    if (!hasHintText && !hasCounter) {
      return;
    }

    return (
      <div class="input-bottom">
        {this.renderHintText()}
        {this.renderCounter()}
      </div>
    );
  }

  private renderLabel() {
    const { label } = this;

    return (
      <div
        class={{
          'label-text-wrapper': true,
          'label-text-wrapper-hidden': !this.hasLabel,
        }}
      >
        {label === undefined ? <slot name="label"></slot> : <div class="label-text">{label}</div>}
      </div>
    );
  }

  /**
   * Returns `true` if label content is provided
   * either by a prop or a content. If you want
   * to get the plaintext value of the label use
   * the `labelText` getter instead.
   */
  private get hasLabel() {
    return this.label !== undefined || this.labelSlot !== null;
  }

  /**
   * Gets any content passed into the `label` slot,
   * not the <slot> definition.
   */
  private get labelSlot() {
    return this.element.querySelector('[slot="label"]');
  }


  /**
   * Renders the border container
   * when fill="outline".
   */
  private renderLabelContainer() {
      const hasOutlineFill = this.fill === 'outline';
  
      if (hasOutlineFill) {
        /**
         * The outline fill has a special outline
         * that appears around the input and the label.
         * Certain stacked and floating label placements cause the
         * label to translate up and create a "cut out"
         * inside of that border by using the notch-spacer element.
         */
        return [
          <div class="input-outline-container">
            <div class="input-outline-start"></div>
            <div
              class={{
                'input-outline-notch': true,
                'input-outline-notch-hidden': !this.hasLabel,
              }}
            >
              <div class="notch-spacer" aria-hidden="true" ref={(el) => (this.notchSpacerEl = el)}>
                {this.label}
              </div>
            </div>
            <div class="input-outline-end"></div>
          </div>,
          this.renderLabel(),
        ];
      }
  
      /**
       * If not using the outline style,
       * we can render just the label.
       */
      return this.renderLabel();
  }

  /**
   * value改变时 - 更新input
   */
   @Watch('value')
   protected valueChanged() {
     const nativeInput = this.nativeInput;
     const value = this.getValue();
     if (nativeInput && nativeInput.value !== value && !this.isComposing) {
       nativeInput.value = value;
     }
     this.emitStyle();
   }



  @Event() fieldEvent: EventEmitter<any>;

  evenFn = (type: string) => (e: Event) => {
    if (!this.inputRef) return;
    if (
      (this.readonly || this.disabled) &&
      (type === "blur" || type === "focus")
    ) return;
    e.stopPropagation();
      
    const { value } = this.inputRef;

    console.log(this.value)
    this.fieldEvent.emit({ detail: { value } })
    if (type === "blur" || type === "change") {
      this.validRules();
    }
  };

  validRules = () => {
    if (!this.errorRef) return;
    const current = this.errorRef;
    if (this.rules && this.rules.length > 0) {
      for (let i = 0; i < this.rules.length; i += 1) {
        const rule = this.rules[i];
        if (rule.validator) {
          if (!rule.validator(this.value) && rule.message) {
            this.errormsg = rule.message;
            if (rule.message) current.style.display = "inline-block";
            return;
          }
        } else if (rule.required && rule.message && !this.value) {
          this.errormsg = rule.message;
          if (rule.message) current.style.display = "inline-block";
          return;
        }
      }
      current.style.display = "none";
      this.errormsg = "";
    }
    if (this.required) {
      current.style.display = !this.value && this.errormsg ? "inline-block" : "none";
    }
  };

  setRules(rules: Rule[]) {
    if (!Array.isArray(rules)) {
      throw new Error("rules need array");
    }
    this.rules = rules;
  }
  
  componentDidLoad() {
    // const root = this.element.shadowRoot;
    // this.inputRef = root.querySelector(".field__ref") as HTMLInputElement
    // this.errorRef = root.querySelector(".field__error__msg")

    this.originalJackeryInput = this.jackeryInput;
  }

  componentDidRender() {
    this.notchController?.calculateNotchWidth();
  }

  componentWillLoad() {
    this.inheritedAttributes = {
      ...inheritAriaAttributes(this.element),
      ...inheritAttributes(this.element, ['tabindex', 'title', 'data-form-type']),
    };
  }

  connectedCallback() {
    const { element } = this;

    this.slotMutationController = createSlotMutationController(element, 'label', () => forceUpdate(this));
    this.notchController = createNotchController(
      element,
      () => this.notchSpacerEl,
      () => this.labelSlot
    );

    this.emitStyle();
    this.debounceChanged();
    if (Build.isBrowser) {
      document.dispatchEvent(
        new CustomEvent('jackeryInputDidLoad', {
          detail: this.element
        })
      );
    }
  }

  disconnectedCallback() {
    if (Build.isBrowser) {
      document.dispatchEvent(
        new CustomEvent('jackeryInputDidUnload', {
          detail: this.element,
        })
      );
    }

    if (this.slotMutationController) {
      this.slotMutationController.destroy();
      this.slotMutationController = undefined;
    }

    if (this.notchController) {
      this.notchController.destroy();
      this.notchController = undefined;
    }
  }

  @Method()
  async setFocus() {
    if (this.nativeInput) {
      this.nativeInput.focus();
    }
  }

  /**
   * Returns the native `<input>` element used under the hood.
   */
  @Method()
  getInputElement(): Promise<HTMLInputElement> {
    return Promise.resolve(this.nativeInput!);
  }

  private renderInput() {
    const { inputId, fill, labelPlacement, readonly, shape, disabled } = this;
    const inItem = hostContext('ion-item', this.element);
    const shouldRenderHighlight = fill !== 'outline' && !inItem;
    return (
      <Host
        data-didInputClearOnEdit={this.didInputClearOnEdit}
        class={{
          'has-focus': this.hasFocus,
          [`input-fill-${fill}`]: fill !== undefined,
          [`input-label-placement-${labelPlacement}`]: true,
          [`input-shape-${shape}`]: shape !== undefined,
          'has-value': this.hasValue(),
          'input-disabled': disabled,
        }}
      >
        <label class="input-wrapper">
          {this.renderLabelContainer()}
          <div class="native-wrapper">
          <input
              id={inputId}
              class="field__ref native-input"
              ref={(input) => (this.nativeInput = input)}
              placeholder={this.placeholder}
              name={this.name}
              pattern={this.pattern}
              min={this.min}
              max={this.max}
              minlength={this.minlength}
              maxlength={this.maxlength}
              multiple={this.multiple}
              disabled={this.disabled}
              readonly={readonly}
              required={this.required}
              spellcheck={this.spellcheck}
              step={this.step}
              size={this.size}
              type={this.type}
              value={this.value}

              autoCapitalize={this.autocapitalize}
              autoComplete={this.autocomplete}
              autoCorrect={this.autocorrect}
              autoFocus={this.autofocus}
              enterKeyHint={this.enterkeyhint}
              inputMode={this.inputmode}

              onChange={this.onChange}
              onBlur={this.onBlur}
              onKeyDown={this.onKeydown}
              onInput={this.onInput}
              onFocus={this.onFocus}

              onClick={this.evenFn("click")}
              onCompositionstart={this.onCompositionStart}
              onCompositionend={this.onCompositionEnd}
              {...this.inheritedAttributes}
            />

            {this.clearInput && !readonly && !this.disabled && (
              <button
                aria-label="reset"
                type="button"
                class="input-clear-icon"
                onPointerDown={(ev) => {
                  ev.preventDefault();
                }}
                onClick={this.clearTextInput}
              >
              <svg xmlns="http://www.w3.org/2000/svg" class="clear-icon" viewBox="0 0 512 512"><path d="M400 145.49L366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49z"></path></svg>
              </button>
            )}
          </div>
          {shouldRenderHighlight && <div class="input-highlight"></div>}
        </label>
        {this.renderBottomContent()}
      </Host>
    );
  }

  render() {
    return this.renderInput()
  }
}

let inputIds = 0;
